import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertCircle, FiClock, FiCheckCircle, FiDollarSign, FiRefreshCw, FiPlus, FiChevronRight, FiAlertTriangle, FiCalendar } from "react-icons/fi";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import CircularProgress from "@mui/material/CircularProgress";
import { remindersApi } from "./remindersApi";
import { showErrorToast } from "../../constants/ToastService";
import { useTheme } from "../../context/ThemeContext";
import "./Reminders.css";

const STATUS_COLORS = {
    ACTIVE: "#4caf50",
    EXPIRING_SOON: "#ff9800",
    EXPIRED: "#f44336",
    RENEWED: "#03a9f4",
    CANCELLED: "#9e9e9e",
};

const PRIORITY_COLORS = {
    CRITICAL: "#f44336",
    HIGH: "#ff9800",
    MEDIUM: "#ffc107",
    LOW: "#4caf50",
};

const formatCurrency = (val) => {
    if (!val && val !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
};

const getDaysLabel = (days) => {
    if (days === null || days === undefined) return "-";
    if (days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return "Today";
    return `${days}d left`;
};

const RemindersDashboard = () => {
    const navigate = useNavigate();
    const { mode } = useTheme();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            const res = await remindersApi.getDashboard();
            if (res.status === "success") setData(res.data);
        } catch (err) {
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    if (loading) {
        return (
            <div className="rem-loading">
                <CircularProgress size={40} sx={{ color: "var(--accent)" }} />
            </div>
        );
    }

    if (!data) return <div className="rem-loading">No data available</div>;

    const { summary, category_stats, priority_stats, upcoming_items, overdue_items, monthly_trend } = data;

    const pieData = (category_stats || []).map((c, i) => ({
        id: i,
        value: c.count,
        label: c.category__name,
        color: c.category__color_code || "#7c5cfc",
    }));

    const barLabels = (monthly_trend || []).map((m) => m.month);
    const barCounts = (monthly_trend || []).map((m) => m.count);
    const barCosts = (monthly_trend || []).map((m) => m.cost / 1000);

    return (
        <div className="rem-page">
            <div className="rem-header">
                <div>
                    <h1 className="rem-title">Reminders</h1>
                    <p className="rem-subtitle">Track bills, licenses, subscriptions & renewals</p>
                </div>
                <div className="rem-header-actions">
                    <button className="rem-btn rem-btn--outline" onClick={fetchDashboard}>
                        <FiRefreshCw size={14} /> Refresh
                    </button>
                    <button className="rem-btn rem-btn--primary" onClick={() => navigate("/reminders/new")}>
                        <FiPlus size={14} /> New Reminder
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="rem-summary-grid">
                <div className="rem-card rem-card--stat" onClick={() => navigate("/reminders/list?status=ACTIVE")}>
                    <div className="rem-card__icon" style={{ background: "rgba(76,175,80,0.15)", color: "#4caf50" }}>
                        <FiCheckCircle size={20} />
                    </div>
                    <div className="rem-card__info">
                        <span className="rem-card__value">{summary.total_active}</span>
                        <span className="rem-card__label">Active</span>
                    </div>
                </div>

                <div className="rem-card rem-card--stat rem-card--warning" onClick={() => navigate("/reminders/list?status=EXPIRING_SOON")}>
                    <div className="rem-card__icon" style={{ background: "rgba(255,152,0,0.15)", color: "#ff9800" }}>
                        <FiAlertCircle size={20} />
                    </div>
                    <div className="rem-card__info">
                        <span className="rem-card__value">{summary.expiring_7_days}</span>
                        <span className="rem-card__label">Expiring in 7 Days</span>
                    </div>
                </div>

                <div className="rem-card rem-card--stat rem-card--danger" onClick={() => navigate("/reminders/list?status=EXPIRED")}>
                    <div className="rem-card__icon" style={{ background: "rgba(244,67,54,0.15)", color: "#f44336" }}>
                        <FiAlertTriangle size={20} />
                    </div>
                    <div className="rem-card__info">
                        <span className="rem-card__value">{summary.expired}</span>
                        <span className="rem-card__label">Expired</span>
                    </div>
                </div>

                <div className="rem-card rem-card--stat" onClick={() => navigate("/reminders/list")}>
                    <div className="rem-card__icon" style={{ background: "rgba(3,169,244,0.15)", color: "#03a9f4" }}>
                        <FiClock size={20} />
                    </div>
                    <div className="rem-card__info">
                        <span className="rem-card__value">{summary.expiring_30_days}</span>
                        <span className="rem-card__label">Due in 30 Days</span>
                    </div>
                </div>

                <div className="rem-card rem-card--stat">
                    <div className="rem-card__icon" style={{ background: "rgba(124,92,252,0.15)", color: "#7c5cfc" }}>
                        <FiDollarSign size={20} />
                    </div>
                    <div className="rem-card__info">
                        <span className="rem-card__value">{formatCurrency(summary.upcoming_cost_90_days)}</span>
                        <span className="rem-card__label">Upcoming Cost (90d)</span>
                    </div>
                </div>

                <div className="rem-card rem-card--stat">
                    <div className="rem-card__icon" style={{ background: "rgba(76,175,80,0.15)", color: "#4caf50" }}>
                        <FiDollarSign size={20} />
                    </div>
                    <div className="rem-card__info">
                        <span className="rem-card__value">{formatCurrency(summary.total_spent_this_year)}</span>
                        <span className="rem-card__label">Spent This Year</span>
                    </div>
                </div>
            </div>

            {/* Charts Row */}
            <div className="rem-charts-row">
                <div className="rem-card rem-card--chart">
                    <h3 className="rem-card__title">Expiry Forecast (6 Months)</h3>
                    {barLabels.length > 0 ? (
                        <BarChart
                            xAxis={[{ scaleType: "band", data: barLabels, tickLabelStyle: { fill: "var(--text-muted)", fontSize: 11 } }]}
                            series={[
                                { data: barCounts, label: "Items", color: "#7c5cfc" },
                                { data: barCosts, label: "Cost (K)", color: "#ff9800" },
                            ]}
                            height={280}
                            sx={{ "& .MuiChartsLegend-label": { fill: "var(--text-secondary) !important", fontSize: "12px !important" } }}
                        />
                    ) : (
                        <div className="rem-empty-chart">No upcoming expirations</div>
                    )}
                </div>

                <div className="rem-card rem-card--chart">
                    <h3 className="rem-card__title">By Category</h3>
                    {pieData.length > 0 ? (
                        <PieChart
                            series={[{
                                data: pieData,
                                innerRadius: 45,
                                outerRadius: 100,
                                paddingAngle: 2,
                                cornerRadius: 4,
                            }]}
                            height={280}
                            sx={{ "& .MuiChartsLegend-label": { fill: "var(--text-secondary) !important", fontSize: "12px !important" } }}
                        />
                    ) : (
                        <div className="rem-empty-chart">No data yet</div>
                    )}
                </div>
            </div>

            {/* Lists Row */}
            <div className="rem-lists-row">
                {/* Upcoming Expirations */}
                <div className="rem-card rem-card--list">
                    <div className="rem-card__list-header">
                        <h3 className="rem-card__title">
                            <FiAlertCircle size={16} style={{ color: "#ff9800" }} /> Expiring Soon
                        </h3>
                        <button className="rem-link" onClick={() => navigate("/reminders/list?status=EXPIRING_SOON")}>
                            View All <FiChevronRight size={14} />
                        </button>
                    </div>
                    {(upcoming_items || []).length === 0 ? (
                        <div className="rem-empty-list">No upcoming expirations</div>
                    ) : (
                        <div className="rem-list">
                            {upcoming_items.map((item) => (
                                <div key={item.id} className="rem-list__item" onClick={() => navigate(`/reminders/${item.id}`)}>
                                    <div className="rem-list__left">
                                        <span className="rem-list__dot" style={{ background: item.category_color || "#7c5cfc" }} />
                                        <div>
                                            <div className="rem-list__name">{item.title}</div>
                                            <div className="rem-list__meta">{item.category_name} {item.provider_name ? `| ${item.provider_name}` : ""}</div>
                                        </div>
                                    </div>
                                    <div className="rem-list__right">
                                        <span className={`rem-badge rem-badge--${item.days_until_expiry <= 7 ? "danger" : "warning"}`}>
                                            {getDaysLabel(item.days_until_expiry)}
                                        </span>
                                        {item.amount && <span className="rem-list__amount">{formatCurrency(item.amount)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Overdue Items */}
                <div className="rem-card rem-card--list">
                    <div className="rem-card__list-header">
                        <h3 className="rem-card__title">
                            <FiAlertTriangle size={16} style={{ color: "#f44336" }} /> Overdue
                        </h3>
                        <button className="rem-link" onClick={() => navigate("/reminders/list?status=EXPIRED")}>
                            View All <FiChevronRight size={14} />
                        </button>
                    </div>
                    {(overdue_items || []).length === 0 ? (
                        <div className="rem-empty-list">No overdue items</div>
                    ) : (
                        <div className="rem-list">
                            {overdue_items.map((item) => (
                                <div key={item.id} className="rem-list__item" onClick={() => navigate(`/reminders/${item.id}`)}>
                                    <div className="rem-list__left">
                                        <span className="rem-list__dot" style={{ background: item.category_color || "#f44336" }} />
                                        <div>
                                            <div className="rem-list__name">{item.title}</div>
                                            <div className="rem-list__meta">{item.category_name} {item.provider_name ? `| ${item.provider_name}` : ""}</div>
                                        </div>
                                    </div>
                                    <div className="rem-list__right">
                                        <span className="rem-badge rem-badge--danger">{getDaysLabel(item.days_until_expiry)}</span>
                                        {item.amount && <span className="rem-list__amount">{formatCurrency(item.amount)}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RemindersDashboard;
